/** @format */

import {
  ApartmentOutlined,
  CompressOutlined,
  DownloadOutlined,
  ExpandOutlined,
  EyeOutlined,
  FullscreenExitOutlined,
  FullscreenOutlined,
  SearchOutlined,
  TagsOutlined,
  TeamOutlined,
  UserAddOutlined,
} from "@ant-design/icons";
import { AutoComplete, Button, Dropdown, Input, Menu, Modal, Result, Select, Space, Spin, Tooltip } from "antd";
import React, {
  CSSProperties,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ReactFlow, {
  Background,
  BackgroundVariant,
  Controls,
  MiniMap,
  Node,
  ReactFlowProvider,
  useReactFlow,
} from "react-flow-renderer";
import { useSelector } from "react-redux";
import { apiOrganigrammaLinkDelete, organigrammaErrorMessage } from "../../../api/apiOrganigramma";
import { usePermission } from "../../../redux-modules/hooks/usePermission";
import { TOrgRole, TOrgStructure } from "../../../redux-modules/declarations/maps/organigramma";
import { selectorLoggedTenance } from "../../../redux-modules/selectors/selectorsAuth";
import { BaseColor } from "../../../theme/maps/Colors";
import { showNotification } from "../../../utils/showNotification";
import { UITitle } from "../../ui/Title";
import UITransition from "../../ui/Transition";
import { DRAG_MIME, OrgChartContext, TOrgChartContext } from "./context";
import { useCardHeights } from "./hooks/useCardHeights";
import { useOrganigrammaData } from "./hooks/useOrganigrammaData";
import { GroupNode } from "./nodes/GroupNode";
import { OrgEdge } from "./nodes/OrgEdge";
import { PersonNode } from "./nodes/PersonNode";
import { CatalogDrawer, TCatalogTab } from "./partials/CatalogDrawer";
import { ExportModal } from "./partials/ExportModal";
import { OrgAvatar } from "./partials/OrgAvatar";
import { PeoplePanel } from "./partials/PeoplePanel";
import { PersonDrawer, TPersonTarget } from "./partials/PersonDrawer";
import { exportOrganigramma, TExportOptions } from "./utils/exportChart";
import {
  ancestorIdsOf,
  buildOrgIndex,
  GROUP_NODE,
  layoutOrganigramma,
  NODE_WIDTH,
  ORG_EDGE,
  PERSON_NODE,
  rectOfPositions,
  subtreeIdsOf,
  TCardPosition,
  TGroupNodeData,
  TOrgEdgeData,
  TPersonNodeData,
} from "./utils/layout";
import { fullNameOf, hexToRgba, searchHaystack } from "./utils/people";
import "./organigramma.css";

const NODE_TYPES = { [PERSON_NODE]: PersonNode, [GROUP_NODE]: GroupNode };
const EDGE_TYPES = { [ORG_EDGE]: OrgEdge };
const FIT_OPTIONS = { padding: 0.15, maxZoom: 1 };

const nextFrames = (count: number): Promise<void> =>
  new Promise(resolve => {
    const step = (left: number): void => {
      if (left <= 0) resolve();
      else requestAnimationFrame(() => step(left - 1));
    };
    step(count);
  });

const OrganigrammaWorkspace = (): JSX.Element => {
  const { checkPermission } = usePermission();
  const canUpdate = checkPermission("Organigramma", "Update");
  const tenance = useSelector(selectorLoggedTenance);
  const reactFlow = useReactFlow();

  const data = useOrganigrammaData();
  const { tree, users, roles, structures, loading, refreshing, error, reload } = data;

  // ---- Stato della vista ---------------------------------------------------
  const [collapsed, setCollapsed] = useState<Set<number>>(new Set());
  const [showStructures, setShowStructures] = useState(true);
  const [showMinimap, setShowMinimap] = useState(true);
  const [search, setSearch] = useState("");
  const [highlightStructureId, setHighlightStructureId] = useState<number | null>(null);
  const [focusedId, setFocusedId] = useState<number | null>(null);
  const [pendingFocusId, setPendingFocusId] = useState<number | null>(null);
  const [panelOpen, setPanelOpen] = useState(true);
  const [fullscreen, setFullscreen] = useState(false);
  const [draggingUserId, setDraggingUserId] = useState<number | null>(null);
  const [canvasDropOver, setCanvasDropOver] = useState(false);

  // ---- Drawer e modali -----------------------------------------------------
  const [personTarget, setPersonTarget] = useState<TPersonTarget | null>(null);
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [catalogTab, setCatalogTab] = useState<TCatalogTab>("roles");
  const [exportOpen, setExportOpen] = useState(false);
  const [exporting, setExporting] = useState(false);

  const workspaceRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLDivElement>(null);
  // Riferimenti sempre aggiornati, da usare negli effect senza farli ripartire
  const reactFlowRef = useRef(reactFlow);
  reactFlowRef.current = reactFlow;

  // ---- Dati derivati -------------------------------------------------------
  const index = useMemo(() => buildOrgIndex(tree), [tree]);

  // Rimuove dai rami compressi chi non esiste più (dopo una modifica)
  useEffect(() => {
    setCollapsed(current => {
      const next = new Set(Array.from(current).filter(id => index.has(id)));
      return next.size === current.size ? current : next;
    });
  }, [index]);

  // Card con nome o ruolo lunghi sono più alte: il layout ne deve tenere conto
  const cardHeights = useCardHeights(workspaceRef, index, canUpdate);

  const layout = useMemo(
    () => layoutOrganigramma(tree, structures, collapsed, showStructures, cardHeights),
    [tree, structures, collapsed, showStructures, cardHeights],
  );
  const layoutRef = useRef(layout);
  layoutRef.current = layout;

  // Comprimere o espandere un ramo ricalcola il layout e la card cliccata cambia
  // posizione nel grafico. Si sposta la vista della stessa distanza, così la card
  // resta ferma sotto il puntatore. Serve useLayoutEffect: con useEffect i nodi
  // verrebbero disegnati nella nuova posizione un fotogramma prima della correzione.
  const collapseAnchorRef = useRef<{ id: string; x: number; y: number } | null>(null);

  useLayoutEffect(() => {
    const anchor = collapseAnchorRef.current;
    if (!anchor) return;
    collapseAnchorRef.current = null;

    const p = layout.positions.get(anchor.id);
    if (!p) return;
    const dx = p.x - anchor.x;
    const dy = p.y - anchor.y;
    if (dx === 0 && dy === 0) return;

    const flow = reactFlowRef.current;
    const { x, y, zoom } = flow.getViewport();
    flow.setViewport({ x: x - dx * zoom, y: y - dy * zoom, zoom });
  }, [layout]);

  const query = search.trim().toLowerCase();

  const searchMatches = useMemo(() => {
    if (!query) return null;
    const ids = new Set<number>();
    index.forEach(({ person }) => {
      if (searchHaystack(person).includes(query)) ids.add(person.id);
    });
    return ids;
  }, [query, index]);

  const structureMatches = useMemo(() => {
    if (highlightStructureId === null) return null;
    const ids = new Set<number>();
    index.forEach(({ person }) => {
      if (person.organigramma_structure_id === highlightStructureId) ids.add(person.id);
    });
    return ids;
  }, [highlightStructureId, index]);

  const activeMatches = useMemo(() => {
    if (searchMatches && structureMatches)
      return new Set(Array.from(searchMatches).filter(id => structureMatches.has(id)));
    return searchMatches || structureMatches;
  }, [searchMatches, structureMatches]);

  const [cleanForExport, setCleanForExport] = useState(false);

  const displayNodes = useMemo((): Node[] => {
    if (cleanForExport || (!activeMatches && focusedId === null)) return layout.nodes;

    return layout.nodes.map(node => {
      if (node.type === GROUP_NODE) {
        const groupData = node.data as TGroupNodeData;
        const dimmed = highlightStructureId !== null && groupData.structure.id !== highlightStructureId;
        return dimmed ? { ...node, data: { ...groupData, dimmed } } : node;
      }
      const personData = node.data as TPersonNodeData;
      const id = personData.person.id;
      const matched = activeMatches ? activeMatches.has(id) : false;
      return {
        ...node,
        data: {
          ...personData,
          highlighted: matched && searchMatches !== null,
          dimmed: activeMatches !== null && !matched,
          focused: focusedId === id,
        },
      };
    });
  }, [layout, activeMatches, searchMatches, focusedId, highlightStructureId, cleanForExport]);

  const displayEdges = useMemo(() => {
    if (cleanForExport || !activeMatches) return layout.edges;
    return layout.edges.map(edge => {
      const dimmed = !activeMatches.has(Number(edge.source)) && !activeMatches.has(Number(edge.target));
      return dimmed ? { ...edge, data: { ...(edge.data as TOrgEdgeData), dimmed } } : edge;
    });
  }, [layout, activeMatches, cleanForExport]);

  const notInChartCount = useMemo(
    () => users.filter(u => !index.has(u.id) && !u.is_disabled).length,
    [users, index],
  );

  // ---- Navigazione nel grafico -------------------------------------------
  const fitToIds = useCallback(
    (ids: number[]) => {
      const visible = ids.map(String).filter(id => layout.positions.has(id));
      if (visible.length === 0) return;
      if (visible.length === 1) {
        const p = layout.positions.get(visible[0]) as TCardPosition;
        reactFlow.setCenter(p.x + NODE_WIDTH / 2, p.y + p.height / 2, { zoom: 1, duration: 500 });
        return;
      }
      const rect = rectOfPositions(visible, layout.positions);
      if (!rect) return;
      // Allarga il riquadro quanto basta perché lo zoom non superi 1x
      const el = canvasRef.current;
      const minWidth = el ? el.clientWidth / 1.4 : 0;
      const minHeight = el ? el.clientHeight / 1.4 : 0;
      const width = Math.max(rect.width, minWidth);
      const height = Math.max(rect.height, minHeight);
      reactFlow.fitBounds(
        {
          x: rect.x - (width - rect.width) / 2,
          y: rect.y - (height - rect.height) / 2,
          width,
          height,
        },
        { padding: 0.2, duration: 500 },
      );
    },
    [layout, reactFlow],
  );

  const fitToIdsRef = useRef(fitToIds);
  fitToIdsRef.current = fitToIds;

  /** Porta una persona al centro, espandendo i rami compressi che la nascondono. */
  const focusPerson = useCallback(
    (personId: number) => {
      const hiddenBy = ancestorIdsOf(index, personId).filter(id => collapsed.has(id));
      if (hiddenBy.length > 0) {
        setCollapsed(current => {
          const next = new Set(current);
          hiddenBy.forEach(id => next.delete(id));
          return next;
        });
      }
      setFocusedId(personId);
      setPendingFocusId(personId);
    },
    [index, collapsed],
  );

  useEffect(() => {
    if (pendingFocusId === null) return;
    const p = layout.positions.get(String(pendingFocusId));
    if (!p) return;
    const flow = reactFlowRef.current;
    flow.setCenter(p.x + NODE_WIDTH / 2, p.y + p.height / 2, {
      zoom: Math.max(flow.getZoom(), 1),
      duration: 600,
    });
    setPendingFocusId(null);
  }, [pendingFocusId, layout]);

  // Ricerca: dopo una breve pausa inquadra i risultati visibili
  useEffect(() => {
    if (!searchMatches || searchMatches.size === 0) return;
    const timer = window.setTimeout(() => fitToIdsRef.current(Array.from(searchMatches)), 450);
    return (): void => window.clearTimeout(timer);
  }, [searchMatches]);

  useEffect(() => {
    if (structureMatches && structureMatches.size > 0) fitToIdsRef.current(Array.from(structureMatches));
  }, [structureMatches]);

  // ---- Azioni sulle persone ----------------------------------------------
  const openPerson = useCallback(
    (personId: number) => {
      setFocusedId(personId);
      setPersonTarget({ mode: "edit", userId: personId });
    },
    [],
  );

  const removePerson = useCallback(
    async (personId: number) => {
      try {
        const response = await apiOrganigrammaLinkDelete(personId);
        showNotification("success", "Organigramma", response.message || "Persona tolta dall'organigramma.");
        setPersonTarget(null);
        if (focusedId === personId) setFocusedId(null);
        await reload();
      } catch (err) {
        showNotification("error", "Operazione non riuscita", organigrammaErrorMessage(err, "Riprova."));
      }
    },
    [reload, focusedId],
  );

  const confirmRemovePerson = useCallback(
    (personId: number) => {
      const entry = index.get(personId);
      if (!entry) return;
      const reports = entry.person.listaDipendenti.length;
      const manager = entry.parentId !== null ? index.get(entry.parentId)?.person : undefined;
      Modal.confirm({
        title: `Togliere ${fullNameOf(entry.person)} dall'organigramma?`,
        content:
          reports > 0
            ? `${reports === 1 ? "Il suo collaboratore diretto passerà" : `I suoi ${reports} collaboratori diretti passeranno`} ${
                manager ? `a ${fullNameOf(manager)}` : "in cima all'organigramma"
              }.`
            : "Potrai ricollocarla in qualsiasi momento dal pannello Persone.",
        okText: "Togli",
        okButtonProps: { danger: true },
        cancelText: "Annulla",
        onOk: () => removePerson(personId),
      });
    },
    [index, removePerson],
  );

  const handleDropUser = useCallback(
    (managerId: number | null, userId: number) => {
      setDraggingUserId(null);
      setCanvasDropOver(false);
      if (!canUpdate) return;

      const entry = index.get(userId);
      if (entry) {
        // Già nel grafico: spostamento sotto un nuovo responsabile
        if (managerId === null) return;
        if (subtreeIdsOf(index, userId).has(managerId)) {
          showNotification(
            "warning",
            "Spostamento non possibile",
            "Una persona non può rispondere a un proprio collaboratore.",
          );
          return;
        }
        if (entry.person.user_manager_id === managerId) {
          showNotification("notification", "Organigramma", `${fullNameOf(entry.person)} è già in questo team.`);
          return;
        }
        setPersonTarget({ mode: "edit", userId, managerId });
        return;
      }

      setPersonTarget({ mode: "create", userId, managerId });
    },
    [canUpdate, index],
  );

  const blockedDropIds = useMemo(
    () =>
      draggingUserId !== null && index.has(draggingUserId)
        ? subtreeIdsOf(index, draggingUserId)
        : new Set<number>(),
    [draggingUserId, index],
  );

  const chartContext: TOrgChartContext = useMemo(
    () => ({
      canUpdate,
      draggingUserId,
      blockedDropIds,
      onOpenPerson: openPerson,
      onAddCollaborator: (managerId: number): void =>
        setPersonTarget({ mode: "create", userId: null, managerId }),
      onToggleCollapse: (personId: number): void => {
        const p = layoutRef.current.positions.get(String(personId));
        collapseAnchorRef.current = p ? { id: String(personId), x: p.x, y: p.y } : null;
        setCollapsed(current => {
          const next = new Set(current);
          if (next.has(personId)) next.delete(personId);
          else next.add(personId);
          return next;
        });
      },
      onRemovePerson: confirmRemovePerson,
      onDropUser: handleDropUser,
    }),
    [canUpdate, draggingUserId, blockedDropIds, openPerson, confirmRemovePerson, handleDropUser],
  );

  // ---- Schermo intero ------------------------------------------------------
  // Mettiamo a schermo intero l'intera pagina (documentElement) e rendiamo il
  // contenitore "fixed": così drawer, modali e notifiche di antd (che vivono
  // in <body>) restano visibili sopra l'organigramma.
  const toggleFullscreen = (): void => {
    if (!fullscreen) {
      setFullscreen(true);
      const root = document.documentElement;
      if (!document.fullscreenElement && root.requestFullscreen) {
        Promise.resolve(root.requestFullscreen()).catch(() => undefined);
      }
    } else {
      setFullscreen(false);
      if (document.fullscreenElement && document.exitFullscreen) {
        Promise.resolve(document.exitFullscreen()).catch(() => undefined);
      }
    }
  };

  useEffect(() => {
    const onChange = (): void => {
      if (!document.fullscreenElement) setFullscreen(false);
    };
    document.addEventListener("fullscreenchange", onChange);
    return (): void => {
      document.removeEventListener("fullscreenchange", onChange);
      if (document.fullscreenElement && document.exitFullscreen) {
        Promise.resolve(document.exitFullscreen()).catch(() => undefined);
      }
    };
  }, []);

  const overlayOpenRef = useRef(false);
  overlayOpenRef.current = Boolean(personTarget) || catalogOpen || exportOpen;

  useEffect(() => {
    // Esc esce anche quando il browser non concede il fullscreen nativo
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape" && fullscreen && !document.fullscreenElement && !overlayOpenRef.current)
        setFullscreen(false);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = fullscreen ? "hidden" : "";
    const timer = window.setTimeout(
      () => reactFlowRef.current.fitView({ ...FIT_OPTIONS, duration: 300 }),
      250,
    );
    return (): void => {
      window.removeEventListener("keydown", onKey);
      window.clearTimeout(timer);
      document.body.style.overflow = "";
    };
  }, [fullscreen]);

  // ---- Export --------------------------------------------------------------
  const legendStructures = useMemo(() => {
    if (!showStructures) return [];
    const used = new Set<number>();
    layout.positions.forEach((_p, id) => {
      const structureId = index.get(Number(id))?.person.organigramma_structure_id;
      if (structureId) used.add(structureId);
    });
    return structures.filter(s => used.has(s.id));
  }, [layout, index, structures, showStructures]);

  const hasStaff = useMemo(
    () => Array.from(layout.positions.keys()).some(id => index.get(Number(id))?.person.is_staff),
    [layout, index],
  );

  const handleExport = async (options: TExportOptions): Promise<void> => {
    const viewportEl = canvasRef.current?.querySelector(".react-flow__viewport") as HTMLElement | null;
    const flowEl = canvasRef.current?.querySelector(".react-flow") as HTMLElement | null;
    if (!viewportEl || !flowEl) return;

    setExporting(true);
    setCleanForExport(true);
    await nextFrames(2);

    try {
      await exportOrganigramma({
        viewportEl,
        containerSize: { width: flowEl.clientWidth, height: flowEl.clientHeight },
        viewport: reactFlow.getViewport(),
        bounds: layout.bounds,
        options,
        title: "Organigramma",
        subtitle: [tenance?.name, options.area === "view" ? "Vista parziale" : null]
          .filter(Boolean)
          .join("  |  "),
        legend: legendStructures,
        hasStaff,
      });
      setExportOpen(false);
      showNotification(
        "success",
        "Export completato",
        options.format === "pdf" ? "Il PDF è stato scaricato." : "L'immagine è stata scaricata.",
      );
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Export organigramma", err);
      showNotification("error", "Export non riuscito", "Non è stato possibile generare il file. Riprova.");
    } finally {
      setCleanForExport(false);
      setExporting(false);
    }
  };

  // ---- Vista: espandi / comprimi --------------------------------------------
  const collapseToFirstLevel = (): void => {
    const next = new Set<number>();
    index.forEach(({ person, depth }) => {
      if (depth === 1 && person.listaDipendenti.length > 0 && !person.is_staff) next.add(person.id);
    });
    setCollapsed(next);
    window.setTimeout(() => reactFlow.fitView({ ...FIT_OPTIONS, duration: 400 }), 60);
  };

  const expandAll = (): void => {
    setCollapsed(new Set());
    window.setTimeout(() => reactFlow.fitView({ ...FIT_OPTIONS, duration: 400 }), 60);
  };

  const viewMenu = (
    <Menu
      onClick={({ key }): void => {
        if (key === "fit") reactFlow.fitView({ ...FIT_OPTIONS, duration: 400 });
        if (key === "expand") expandAll();
        if (key === "collapse") collapseToFirstLevel();
        if (key === "structures") setShowStructures(v => !v);
        if (key === "minimap") setShowMinimap(v => !v);
      }}
      items={[
        { key: "fit", icon: <EyeOutlined />, label: "Adatta alla finestra" },
        { key: "expand", icon: <ExpandOutlined />, label: "Espandi tutti i rami" },
        { key: "collapse", icon: <CompressOutlined />, label: "Comprimi al primo livello" },
        { type: "divider" as const },
        { key: "structures", label: showStructures ? "Nascondi i riquadri delle strutture" : "Mostra i riquadri delle strutture" },
        { key: "minimap", label: showMinimap ? "Nascondi la minimappa" : "Mostra la minimappa" },
      ]}
    />
  );

  // ---- Ricerca: suggerimenti ------------------------------------------------
  const searchOptions = useMemo(() => {
    if (!searchMatches) return [];
    return Array.from(searchMatches)
      .slice(0, 8)
      .map(id => {
        const person = index.get(id)?.person;
        if (!person) return null;
        return {
          value: String(id),
          label: (
            <div className="org-search-option">
              <OrgAvatar person={person} size={24} />
              <div className="org-search-option__text">
                <span className="org-search-option__name">{fullNameOf(person)}</span>
                <span className="org-search-option__role">{person.role_name}</span>
              </div>
            </div>
          ),
        };
      })
      .filter(Boolean) as { value: string; label: JSX.Element }[];
  }, [searchMatches, index]);

  // ---- Drag & drop sul canvas vuoto (nuovo vertice) --------------------------
  const canDropOnCanvas = canUpdate && draggingUserId !== null && !index.has(draggingUserId);

  const accentStyle = {
    "--org-accent": BaseColor || "#1890ff",
    "--org-accent-soft": hexToRgba(BaseColor, 0.08),
    "--org-accent-ring": hexToRgba(BaseColor, 0.22),
  } as CSSProperties;

  // ---- Render ----------------------------------------------------------------
  const toolbar = (
    <div className="org-toolbar">
      <div className="org-toolbar__title">
        <UITitle level={fullscreen ? 4 : 2} color={BaseColor}>
          Organigramma
        </UITitle>
        {!loading && !error && (
          <span className="org-toolbar__stats">
            {index.size === 1 ? "1 persona" : `${index.size} persone`} nell'organigramma
            {notInChartCount > 0 && `, ${notInChartCount} da collocare`}
          </span>
        )}
      </div>
      <Space wrap>
        {canUpdate && (
          <Button
            icon={<TagsOutlined />}
            onClick={(): void => setCatalogOpen(true)}
            disabled={loading || Boolean(error)}
          >
            Ruoli e strutture
          </Button>
        )}
        {canUpdate && (
          <Button
            type="primary"
            icon={<UserAddOutlined />}
            onClick={(): void => setPersonTarget({ mode: "create", userId: null, managerId: null })}
            disabled={loading || Boolean(error)}
          >
            Aggiungi persona
          </Button>
        )}
        <Button
          icon={<DownloadOutlined />}
          onClick={(): void => setExportOpen(true)}
          disabled={loading || Boolean(error) || index.size === 0}
        >
          Esporta
        </Button>
        <Tooltip title={fullscreen ? "Esci da schermo intero (Esc)" : "Schermo intero"}>
          <Button
            icon={fullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
            onClick={toggleFullscreen}
            aria-label={fullscreen ? "Esci da schermo intero" : "Schermo intero"}
          />
        </Tooltip>
      </Space>
    </div>
  );

  let body: JSX.Element;

  if (loading) {
    body = (
      <div className="org-body">
        <div className="org-state">
          <Spin tip="Caricamento dell'organigramma..." />
        </div>
      </div>
    );
  } else if (error) {
    body = (
      <div className="org-body">
        <div className="org-state">
          <Result
            status="warning"
            title="Impossibile caricare l'organigramma"
            subTitle={error}
            extra={
              <Button type="primary" onClick={(): void => void reload()}>
                Riprova
              </Button>
            }
          />
        </div>
      </div>
    );
  } else {
    body = (
      <div className="org-body">
        {panelOpen && (
          <PeoplePanel
            users={users}
            index={index}
            canUpdate={canUpdate}
            focusedId={focusedId}
            onClose={(): void => setPanelOpen(false)}
            onPlace={(userId): void => setPersonTarget({ mode: "create", userId, managerId: null })}
            onLocate={focusPerson}
            onDragStart={setDraggingUserId}
            onDragEnd={(): void => {
              setDraggingUserId(null);
              setCanvasDropOver(false);
            }}
          />
        )}

        <div
          className="org-canvas"
          ref={canvasRef}
          onDragOver={(e): void => {
            if (!canDropOnCanvas) return;
            e.preventDefault();
            if (!canvasDropOver) setCanvasDropOver(true);
          }}
          onDragLeave={(e): void => {
            if (e.currentTarget === e.target) setCanvasDropOver(false);
          }}
          onDrop={(e): void => {
            if (!canDropOnCanvas) return;
            e.preventDefault();
            const userId = Number(e.dataTransfer.getData(DRAG_MIME) || draggingUserId);
            if (userId) handleDropUser(null, userId);
          }}
        >
          <OrgChartContext.Provider value={chartContext}>
            <ReactFlow
              nodes={displayNodes}
              edges={displayEdges}
              nodeTypes={NODE_TYPES}
              edgeTypes={EDGE_TYPES}
              nodesDraggable={false}
              nodesConnectable={false}
              elementsSelectable={false}
              panOnDrag
              zoomOnScroll
              zoomOnPinch
              zoomOnDoubleClick={false}
              minZoom={0.1}
              maxZoom={1.5}
              fitView
              fitViewOptions={FIT_OPTIONS}
              onPaneClick={(): void => setFocusedId(null)}
              attributionPosition="bottom-right"
            >
              <Background variant={BackgroundVariant.Dots} gap={22} size={1.2} color="#d6dbe3" />
              <Controls showInteractive={false} fitViewOptions={FIT_OPTIONS} />
              {showMinimap && index.size > 0 && (
                <MiniMap
                  nodeColor={(node): string =>
                    node.type === GROUP_NODE
                      ? (node.data as TGroupNodeData).structure.background_color
                      : hexToRgba(BaseColor, 0.55)
                  }
                  nodeStrokeColor={(node): string =>
                    node.type === GROUP_NODE ? (node.data as TGroupNodeData).structure.text_color : "#ffffff"
                  }
                  nodeBorderRadius={6}
                  maskColor="rgba(240, 242, 245, 0.7)"
                />
              )}
            </ReactFlow>
          </OrgChartContext.Provider>

          <div className="org-overlay org-overlay--top-left">
            {!panelOpen && (
              <Tooltip title="Mostra il pannello Persone">
                <Button
                  className="org-panel-toggle"
                  icon={<TeamOutlined />}
                  onClick={(): void => setPanelOpen(true)}
                >
                  Persone
                </Button>
              </Tooltip>
            )}
            <AutoComplete
              className="org-search"
              value={search}
              options={searchOptions}
              onChange={(value: string): void => setSearch(value)}
              onSelect={(value: string): void => {
                setSearch("");
                focusPerson(Number(value));
              }}
              notFoundContent={query ? "Nessuna persona trovata" : null}
            >
              <Input
                allowClear
                prefix={<SearchOutlined style={{ color: "rgba(0,0,0,.35)" }} />}
                placeholder="Cerca nell'organigramma per nome o ruolo"
                suffix={
                  searchMatches ? (
                    <span className="org-search__count">
                      {searchMatches.size === 1 ? "1 risultato" : `${searchMatches.size} risultati`}
                    </span>
                  ) : (
                    <span />
                  )
                }
              />
            </AutoComplete>
          </div>

          <div className="org-overlay org-overlay--top-right">
            {structures.length > 0 && (
              <Select<number>
                allowClear
                style={{ width: 210 }}
                placeholder="Evidenzia una struttura"
                value={highlightStructureId ?? undefined}
                onChange={(value): void => setHighlightStructureId(value ?? null)}
                onClear={(): void => setHighlightStructureId(null)}
                options={structures.map(s => ({
                  value: s.id,
                  label: (
                    <span className="org-structure-option">
                      <span
                        className="org-structure-option__dot"
                        style={{ background: s.background_color, borderColor: s.text_color }}
                      />
                      {s.name}
                    </span>
                  ),
                }))}
              />
            )}
            <Dropdown overlay={viewMenu} trigger={["click"]} placement="bottomRight">
              <Button icon={<ApartmentOutlined />}>Vista</Button>
            </Dropdown>
          </div>

          {refreshing && (
            <div className="org-overlay org-overlay--center">
              <Spin size="small" />
            </div>
          )}

          <div className="org-legend">
            <span className="org-legend__item">
              <svg width="26" height="8" aria-hidden="true">
                <line x1="0" y1="4" x2="26" y2="4" stroke="#a0aab8" strokeWidth="2" />
              </svg>
              Linea gerarchica
            </span>
            <span className="org-legend__item">
              <svg width="26" height="8" aria-hidden="true">
                <line x1="0" y1="4" x2="26" y2="4" stroke="#d9a441" strokeWidth="2" strokeDasharray="5 4" />
              </svg>
              Staff
            </span>
          </div>

          {index.size === 0 && (
            <div className={`org-empty${canvasDropOver ? " is-drop-target" : ""}`}>
              <Result
                icon={<ApartmentOutlined style={{ color: BaseColor }} />}
                title="L'organigramma è vuoto"
                subTitle={
                  canUpdate
                    ? "Trascina qui una persona dal pannello per metterla al vertice, oppure aggiungila."
                    : "Non è ancora stata collocata nessuna persona."
                }
                extra={
                  canUpdate && (
                    <Button
                      type="primary"
                      icon={<UserAddOutlined />}
                      onClick={(): void => setPersonTarget({ mode: "create", userId: null, managerId: null })}
                    >
                      Aggiungi la prima persona
                    </Button>
                  )
                }
              />
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      ref={workspaceRef}
      className={`org-workspace${fullscreen ? " is-fullscreen" : ""}`}
      style={accentStyle}
    >
      {toolbar}
      {body}

      <PersonDrawer
        target={personTarget}
        canUpdate={canUpdate}
        users={users}
        index={index}
        roles={roles}
        structures={structures}
        onRoleCreated={data.addRole}
        onStructureCreated={data.addStructure}
        onClose={(): void => setPersonTarget(null)}
        onSaved={reload}
        onRemove={removePerson}
        onOpenPerson={(personId): void => {
          focusPerson(personId);
          setPersonTarget({ mode: "edit", userId: personId });
        }}
      />

      <CatalogDrawer
        open={catalogOpen}
        tab={catalogTab}
        onTabChange={setCatalogTab}
        onClose={(): void => setCatalogOpen(false)}
        roles={roles}
        structures={structures}
        canUpdate={canUpdate}
        onChanged={reload}
        onShowRole={(role: TOrgRole): void => {
          setCatalogOpen(false);
          setHighlightStructureId(null);
          setSearch(role.name);
        }}
        onShowStructure={(structure: TOrgStructure): void => {
          setCatalogOpen(false);
          setSearch("");
          setShowStructures(true);
          setHighlightStructureId(structure.id);
        }}
      />

      <ExportModal
        open={exportOpen}
        exporting={exporting}
        onCancel={(): void => setExportOpen(false)}
        onExport={handleExport}
      />
    </div>
  );
};

export const ViewOrganigramma = (): JSX.Element => (
  <UITransition>
    <ReactFlowProvider>
      <OrganigrammaWorkspace />
    </ReactFlowProvider>
  </UITransition>
);